import React, { Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay/hooks';

import Grid, { GridProps } from '@material-ui/core/Grid';
import ChatIcon from '@material-ui/icons/Chat';
import FastRewindIcon from '@material-ui/icons/FastRewind';
import FeedbackIcon from '@material-ui/icons/Feedback';
import SubjectIcon from '@material-ui/icons/Subject';
import { graphql } from 'babel-plugin-relay/macro';
import { ErrorBoundary } from 'core';
import moment from 'moment';
import { fetchQuery } from 'relay-runtime';

import { useQueryOptions } from '@lib/hooks';

import {
  CardMessagingActivityQueued,
  CardMessagingActivityReceived,
  CardMessagingActivitySent,
  CardMessagingActivityUndelivered,
} from 'deprecated/fragments';

import { CardMessagingActivity, ContentLoaderTypography, SvgIconWrapper } from 'components/v2';
import { MessagingActivityRoutes } from '@InsightsCenter/modules/MessagingActivity/interfaces/MessagingActivityRoutes';
import { MessagingActivityTabs } from '@InsightsCenter/modules/MessagingActivity/constants/MessagingActivityTabs';

import { GridMessagingActivitySummaryQuery } from './__generated__/GridMessagingActivitySummaryQuery.graphql';

const messages = defineMessages({
  sentMessages: {
    id: 'deprecated.queries.GridMessagingActivitySummary.sentMessages',
    defaultMessage: 'Sent Messages',
  },
  undeliveredMessages: {
    id: 'deprecated.queries.GridMessagingActivitySummary.undeliveredMessages',
    defaultMessage: 'Undelivered Messages',
  },
  patientResponses: {
    id: 'deprecated.queries.GridMessagingActivitySummary.patientResponses',
    defaultMessage: 'Patient Responses',
  },
  nextReminders: {
    id: 'deprecated.queries.GridMessagingActivitySummary.nextReminders',
    defaultMessage: 'Next Reminders',
  },
});

const query = graphql`
  query GridMessagingActivitySummaryQuery($dateFrom: Date!, $sendAtFrom: Date!, $dateTo: Date!) {
    ...CardMessagingActivitySent_Query @arguments(createdAtFrom: $dateFrom, createdAtTo: $dateTo)
    ...CardMessagingActivityUndelivered_Query @arguments(createdAtFrom: $dateFrom, createdAtTo: $dateTo)
    ...CardMessagingActivityReceived_Query @arguments(createdAtFrom: $dateFrom, createdAtTo: $dateTo)
    ...CardMessagingActivityQueued_Query @arguments(sendAtFrom: $sendAtFrom, sendAtTo: $dateTo)
  }
`;

export interface GridMessagingActivitySummaryProps extends Omit<GridProps, 'classes'> {
  dateFrom: number;
  sendAtFrom: number;
  dateTo: number;
  onRedirect: (redirect: MessagingActivityRoutes) => void;
}

const GridMessagingActivitySummary: React.FC<GridMessagingActivitySummaryProps> = ({
  dateFrom,
  sendAtFrom,
  dateTo,
  onRedirect,
  ...rest
}) => {
  const data = useLazyLoadQuery<GridMessagingActivitySummaryQuery>(query, { dateFrom, sendAtFrom, dateTo });

  const { formatMessage } = useIntl();
  const environment = useRelayEnvironment();
  const { fetchKey } = useQueryOptions('ChatActivity');

  useEffect(() => {
    if (typeof fetchKey === 'number' && fetchKey % 10 === 0) {
      fetchQuery(environment, query, { dateFrom, sendAtFrom: moment().valueOf(), dateTo });
    }
  }, [fetchKey, environment, dateFrom, dateTo]);

  return (
    <Grid container spacing={16} {...rest}>
      <Grid item xs>
        <CardMessagingActivitySent
          title={formatMessage(messages.sentMessages)}
          icon={<SvgIconWrapper Icon={FastRewindIcon} color="success" fontSize="small" includeBackground />}
          onClick={() => onRedirect('sentMessages')}
          fragmentRef={data}
        />
      </Grid>
      <Grid item xs>
        <CardMessagingActivityUndelivered
          title={formatMessage(messages.undeliveredMessages)}
          icon={<SvgIconWrapper Icon={FeedbackIcon} color="error" fontSize="small" includeBackground />}
          onClick={() => onRedirect('undeliveredMessages')}
          fragmentRef={data}
        />
      </Grid>
      <Grid item xs>
        <CardMessagingActivityReceived
          title={formatMessage(messages.patientResponses)}
          icon={<SvgIconWrapper Icon={ChatIcon} color="info" fontSize="small" includeBackground />}
          onClick={() => onRedirect('receivedMessages')}
          fragmentRef={data}
        />
      </Grid>
      <Grid item xs>
        <CardMessagingActivityQueued
          title={formatMessage(messages.nextReminders)}
          icon={<SvgIconWrapper Icon={SubjectIcon} color="default" fontSize="small" includeBackground />}
          onClick={() => onRedirect(MessagingActivityTabs.Queued)}
          fragmentRef={data}
        />
      </Grid>
    </Grid>
  );
};

export default (props: GridMessagingActivitySummaryProps) => {
  const { formatMessage } = useIntl();

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <Grid container spacing={16}>
            <Grid item xs>
              <CardMessagingActivity
                title={formatMessage(messages.sentMessages)}
                icon={<SvgIconWrapper Icon={FastRewindIcon} color="success" fontSize="small" includeBackground />}
                count={<ContentLoaderTypography />}
              />
            </Grid>
            <Grid item xs>
              <CardMessagingActivity
                title={formatMessage(messages.undeliveredMessages)}
                icon={<SvgIconWrapper Icon={FeedbackIcon} color="error" fontSize="small" includeBackground />}
                count={<ContentLoaderTypography />}
              />
            </Grid>
            <Grid item xs>
              <CardMessagingActivity
                title={formatMessage(messages.patientResponses)}
                icon={<SvgIconWrapper Icon={ChatIcon} color="info" fontSize="small" includeBackground />}
                count={<ContentLoaderTypography />}
              />
            </Grid>
            <Grid item xs>
              <CardMessagingActivity
                title={formatMessage(messages.nextReminders)}
                icon={<SvgIconWrapper Icon={SubjectIcon} color="default" fontSize="small" includeBackground />}
                count={<ContentLoaderTypography />}
              />
            </Grid>
          </Grid>
        }
      >
        <GridMessagingActivitySummary {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
